import styled, { keyframes, css } from 'styled-components'
import { motion } from 'framer-motion'
import {
    svgStroke,
    fromTablet, belowLaptop, belowTablet, iOSSafari, lightMode, darkMode
} from 'utilities/styled'
import { Content as ModalContent } from 'modals/dialog/s'
import SearchBase from 'components/form/input/text'
import { H4 } from 'components/typography/heading'
import { UnorderedList } from 'components/list'
import { Naked } from 'components/button'
import { captionSmallCss } from 'components/typography/paragraph'
import { Static } from 'components/empty'

const bgSpin = keyframes`
    to {
        --border-angle: 1turn;
    }
`

export const CommandBarContent = styled(ModalContent)`
    ${belowTablet`
        --__modal-content-margin: 0 !important;
    `}
`

export const DragArea = styled.div`
    ${belowLaptop`
        display: none;
    `}

    @media (hover: hover) {
        cursor: grab;

        display: grid;
        place-items: center;

        margin-inline-end: -20px;
        width: 64px;
        height: 64px;

        color: var(--huma-color-foreground-subtle);

        &:active {
            cursor: grabbing;

            color: var(--huma-color-foreground-default);
        }
    }
`

export const Container = styled.div`
    --command-search-height: 64px;
    --border-angle: 0turn;
    --border-size: 2px;
    --border-radius: 8px;
    --blur-radius: 2em;

    position: relative;
    anchor-name: --command-bar;

    border: var(--border-size) solid transparent;
    border-radius: var(--border-radius);
    width: 750px;
    padding: 8px;
    background-color: rgb(from var(--huma-color-surface-default) r g b / .97);
    backdrop-filter: blur(8px);
    box-shadow:
        0 12px 24px 0 rgb(from var(--huma-palette-neutral-10) r g b / .2),
        0 0 32px 0 rgb(from var(--huma-palette-neutral-10) r g b / .1);

    transition: box-shadow .2s;

    ${darkMode`
        outline: 1px solid var(--huma-color-border-default);
        box-shadow:
            0 12px 24px 0 var(--huma-palette-neutral-10),
            0 0 16px 0 var(--huma-palette-neutral-10),
            0 0 32px 0 var(--huma-palette-neutral-10);
    `}

    &::before,
    &::after {
        --full-opaque: conic-gradient(red 0 0); /* Full opaque coverage */

        @property --border-angle {
            syntax: "<angle>";
            inherits: false;
            initial-value: 0turn;
        }

        content: "";
        pointer-events: none;
        position: absolute;
        inset: calc(-1 * var(--border-size));

        border: inherit;
        border-radius: inherit;
        background: conic-gradient(
            from var(--border-angle),
            var(--huma-palette-purple-50) 2.76%,
            var(--huma-palette-purple-80) 16.87%,
            var(--huma-palette-skin-50) 37.54%,
            var(--huma-palette-yellow-60) 49.64%,
            var(--huma-palette-green-50) 61.24%,
            var(--huma-palette-green-50) 71.32%,
            var(--huma-palette-blue-80) 87.46%,
            var(--huma-palette-blue-40) 96.53%,
            var(--huma-palette-purple-50) 100%
        ) border-box;
        box-shadow: 0 0 calc(3 * var(--blur-radius)) rgba(0 0 0 / .001);

        mask:
            var(--full-opaque) no-clip subtract,
            var(--full-opaque) padding-box; /* Make everything inside padding-box transparent by subtracting padding-box from no-clip box */
        opacity: 0;

        transition: opacity .4s;
        animation: ${bgSpin} 3s infinite linear;
    }

    &::after {
        filter: blur(var(--blur-radius));
    }

    &:has(${DragArea}:active) {
        ${lightMode`
            box-shadow:
                0 4px 8px 0 rgb(from var(--huma-palette-neutral-10) r g b / .2),
                0 0 16px 0 rgb(from var(--huma-palette-neutral-10) r g b / .1);
        `}
    }

    &.loading {
        &::before { opacity: 1; }
        &::after { opacity: 0.5; }
    }

    ${belowTablet`
        --command-search-height: 40px;

        margin-block: 0;
        border-radius: 0;
        border: unset;
        max-width: 100vw;
        width: 100%;
        max-height: calc(100vh - env(keyboard-inset-height, 0px));
        padding-block-end: 0;
        padding-inline: 0;
        transform: unset !important;

        hr {
            margin-block: 0;
            margin-inline: -8px;
            width: 100vw;
        }
    `}

    ${iOSSafari`
        max-height: var(--viewport-height);
    `}
`

export const SearchWrapper = styled.div`
    position: relative;

    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    grid-auto-flow: column;
    column-gap: 16px;
    align-items: center;

    padding-inline: 12px;

    svg {
        ${svgStroke()}
    }

    &.active {
        padding-block-end: 12px;

        &::after {
            content: "";
            position: absolute;
            z-index: -1;
            inset-block-end: 0;
            inset-inline: -10px;

            height: 1px;
            background-color: var(--huma-color-border-default);
        }
    }

    ${belowTablet`
        column-gap: 8px;

        margin-block-end: 8px;
    `}
`

export const Search = styled(SearchBase)`
    border: none;
    outline: none;
    box-shadow: none !important;
    height: var(--command-search-height);
    padding: 0;

    font-size: 24px;

    ${belowTablet`
        font-size: 18px;
    `}
`

export const Shortcuts = styled.div`
    position: relative;

    display: flex;
    justify-content: space-between;
    align-items: center;
    column-gap: 24px;

    padding-block-start: 8px;
    padding-inline: 8px;

    &::before {
        content: "";
        position: absolute;
        z-index: -1;
        inset-block-start: 0;
        inset-inline: -10px;

        height: 1px;
        background-color: var(--huma-color-border-default);
    }
`

export const ShortcutsSection = styled.div`
    display: flex;
    gap: 24px;

    > :not(:last-child) {
        position: relative;

        &::after {
            content: "";
            position: absolute;
            inset-block-start: 50%;
            inset-inline-end: -12px;

            height: 16px;
            width: 1px;
            background-color: var(--huma-color-border-default);

            transform: translateY(-50%);
        }
    }
`

const shortcutGroupCss = css`
    display: flex;
    align-items: baseline;
    gap: 8px;
`

export const ShortcutGroup = styled(motion.div).attrs({
    variants: {
        out: {
            opacity: 0,
            transition: {
                duration: .1
            }
        },
        in: { opacity: 1 }
    },
    initial: 'out',
    animate: 'in',
    exit: 'out'
})`
    ${shortcutGroupCss}
`

export const ShortcutGroupClickable = styled(Naked)`
    ${shortcutGroupCss}

    border-radius: 4px;
    padding: 4px;

    &:hover {
        background-color: var(--huma-color-surface-subtle);
    }

    &:is(:active, .active) {
        background-color: var(--huma-color-surface-subtle-hover);
    }
`

export const ShortcutLabel = styled.span`
    ${captionSmallCss}
`

export const Toast = styled(motion.span).attrs({
    variants: {
        out: {
            opacity: 0,
            transition: {
                duration: .5
            }
        },
        in: {
            opacity: 1,
            transition: {
                duration: .1
            }
        }
    },
    initial: 'out',
    animate: 'in',
    exit: 'out'
})`
    position: relative;
    inset-block-start: 2px;
    inset-inline-start: 4px;

    display: inline-flex;
    align-items: center;
    gap: 8px;

    min-width: 0;
    padding-inline-start: 24px;

    white-space: nowrap;

    .message {
        font-size: 14px;
        font-weight: 500;
    }

    .label {
        ${captionSmallCss}

        overflow: hidden;
        text-overflow: ellipsis;
    }

    &::before {
        content: "";
        position: absolute;
        inset-block-start: 6px;
        inset-inline-start: 5px;

        border-radius: 50%;
        width: 6px;
        height: 6px;
        background-color: var(--huma-palette-green-70);
        box-shadow: 0 0 12px 4px light-dark(
            var(--huma-palette-green-60),
            var(--huma-palette-green-40)
        );
    }
`

export const ResultsWrapper = styled.div`
    --item-height: 48px;

    overflow-y: auto;
    max-height: calc(10 * var(--item-height) + 16px);
    padding: 8px;

    ${fromTablet`
        margin-inline: -8px;

        &:has(> li:nth-child(11)) {
            max-height: calc(10 * var(--item-height) + 32px);
        }
    `}

    ${belowTablet`
        max-height: calc(100vh - env(keyboard-inset-height, 0px) - 58px);
    `}

    ${iOSSafari`
        max-height: calc(var(--viewport-height) - 56px);
    `}
`

export const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;

    margin: 24px 8px 8px;

    &:first-child {
        margin-block-start: 8px;
    }

    ${belowTablet`
        margin: 16px 16px 0;
    `}
`

export const SectionHeading = styled(H4)`
    font-size: 14px;
    font-weight: 400;
    color: var(--huma-color-foreground-subtle);
`

export const Results = styled(UnorderedList)`
    position: relative;
`

export const Empty = styled(Static)`
    min-height: var(--item-height);
    padding-block: 0;
`